import React, { useEffect } from 'react';
import { useParams, Link, useNavigate } from 'react-router-dom';
import { motion } from 'framer-motion';
import { ArrowLeft, Calendar, User, Clock, Download, FileText } from 'lucide-react';
import { useArticle } from '../hooks/useApi';
import analytics from '../utils/analytics';
import { usePageTitle } from '../hooks/usePageTitle';

const ArticleDetailPage: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const { data: article, loading, error } = useArticle(id || '');

  // 设置页面标题和描述
  usePageTitle(
    article ? article.title : '文章详情',
    article ? article.excerpt : '查看技术交流文章的详细内容'
  );

  // 文章查看埋点
  useEffect(() => {
    if (article) {
      analytics.article.view(article.title, article.category);
    }
  }, [article]);

  const handleDownload = (pdfUrl: string, filename: string) => {
    // 埋点：PDF下载
    if (article) {
      analytics.downloadPDF(article.title, pdfUrl);
    }

    // 创建一个隐藏的 a 标签来触发下载
    const link = document.createElement('a');
    link.href = pdfUrl;
    link.download = filename;
    link.target = '_blank';
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };

  // 加载状态
  if (loading) {
    return (
      <div className="pt-16 min-h-screen bg-gray-50">
        <div className="container-max section-padding">
          <div className="flex justify-center items-center py-20">
            <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary-600"></div>
            <span className="ml-4 text-gray-600">加载中...</span>
          </div>
        </div>
      </div>
    );
  }

  // 错误状态或文章不存在
  if (error || !article) {
    return (
      <div className="pt-16 min-h-screen bg-gray-50">
        <div className="container-max section-padding">
          <div className="text-center py-20">
            <FileText className="w-16 h-16 mx-auto text-gray-400 mb-4" />
            <h3 className="text-xl font-semibold text-gray-600 mb-2">
              文章不存在
            </h3>
            <p className="text-gray-500 mb-4">
              抱歉，您查找的文章不存在或已被删除
            </p>
            <button
              onClick={() => navigate('/articles')}
              className="btn-primary"
            >
              返回文章列表
            </button>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="pt-16 min-h-screen bg-gray-50">
      {/* 返回按钮 */}
      <div className="bg-white border-b">
        <div className="container-max py-4">
          <Link
            to="/articles"
            className="inline-flex items-center text-gray-600 hover:text-primary-600 transition-colors"
          >
            <ArrowLeft className="w-4 h-4 mr-2" />
            返回文章列表
          </Link>
        </div>
      </div>

      {/* 文章内容 */}
      <div className="container-max section-padding">
        <motion.article
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6 }}
          className="max-w-4xl mx-auto"
        >
          {/* 文章头部 */}
          <div className="bg-white rounded-xl shadow-sm p-8 mb-6">
            <h1 className="text-3xl md:text-4xl font-bold text-gray-900 mb-4">
              {article.title}
            </h1>

            {article.title_en && (
              <p className="text-lg text-gray-600 mb-6">
                {article.title_en}
              </p>
            )}

            {/* 文章元信息 */}
            <div className="flex flex-wrap items-center gap-4 text-sm text-gray-600 mb-6">
              {article.author && (
                <div className="flex items-center">
                  <User className="w-4 h-4 mr-1" />
                  <span>{article.author}</span>
                </div>
              )}
              {article.publishedAt && (
                <div className="flex items-center">
                  <Calendar className="w-4 h-4 mr-1" />
                  <span>{article.publishedAt}</span>
                </div>
              )}
              {article.readTime && (
                <div className="flex items-center">
                  <Clock className="w-4 h-4 mr-1" />
                  <span>{article.readTime}</span>
                </div>
              )}
            </div>

            {/* 标签 */}
            {article.tags && article.tags.length > 0 && (
              <div className="flex flex-wrap gap-2 mb-6">
                {article.tags.map((tag, index) => (
                  <span
                    key={index}
                    className="px-3 py-1 bg-primary-50 text-primary-700 rounded-full text-sm"
                  >
                    {tag}
                  </span>
                ))}
              </div>
            )}

            {/* PDF 下载按钮 */}
            {article.pdfFile && (
              <div className="bg-blue-50 border border-blue-200 rounded-lg p-4 mb-6">
                <div className="flex items-start justify-between">
                  <div className="flex items-start">
                    <FileText className="w-5 h-5 text-blue-600 mr-3 mt-0.5" />
                    <div>
                      <h3 className="font-semibold text-gray-900 mb-1">
                        完整文档
                      </h3>
                      <p className="text-sm text-gray-600 mb-3">
                        下载完整的PDF文档以获取详细内容
                      </p>
                    </div>
                  </div>
                  <button
                    onClick={() => handleDownload(article.pdfFile!, `${article.title}.pdf`)}
                    className="inline-flex items-center px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors duration-200 whitespace-nowrap"
                  >
                    <Download className="w-4 h-4 mr-2" />
                    下载PDF
                  </button>
                </div>
              </div>
            )}
          </div>

          {/* 文章主体内容 */}
          <div className="bg-white rounded-xl shadow-sm p-8">
            {article.excerpt && (
              <div className="bg-gray-50 border-l-4 border-primary-500 p-4 mb-6">
                <p className="text-gray-700 italic">{article.excerpt}</p>
              </div>
            )}

            <div className="prose prose-lg max-w-none">
              <div className="text-gray-700 leading-relaxed whitespace-pre-line">
                {article.content}
              </div>
            </div>

            {article.content_en && (
              <div className="mt-8 pt-8 border-t">
                <h3 className="text-xl font-semibold text-gray-900 mb-4">
                  English Version
                </h3>
                <div className="text-gray-700 leading-relaxed whitespace-pre-line">
                  {article.content_en}
                </div>
              </div>
            )}
          </div>

          {/* 底部操作 */}
          <div className="mt-8 text-center">
            <Link
              to="/articles"
              className="btn-secondary inline-flex items-center"
            >
              <ArrowLeft className="w-4 h-4 mr-2" />
              返回文章列表
            </Link>
          </div>
        </motion.article>
      </div>
    </div>
  );
};

export default ArticleDetailPage;
